import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { templatesApi } from '../api/index.js';
import type { TemplateSummaryDto } from '../types.js';

function PreviewModal({ template, onClose }: { template: TemplateSummaryDto; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 'var(--radius)',
          width: 'min(900px, 95vw)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,.25)',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '.75rem 1rem', borderBottom: '1px solid var(--color-border)',
        }}>
          <span style={{ fontWeight: 600 }}>{template.title}</span>
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            <a
              href={templatesApi.htmlUrl(template.id)}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline"
              style={{ fontSize: '.8rem', padding: '.3rem .6rem' }}
            >
              Open ↗
            </a>
            <button
              onClick={onClose}
              className="btn btn-outline"
              style={{ fontSize: '.8rem', padding: '.3rem .6rem' }}
            >
              ✕ Close
            </button>
          </div>
        </div>
        <iframe
          src={templatesApi.htmlUrl(template.id)}
          title={`Preview: ${template.title}`}
          sandbox="allow-same-origin"
          style={{ flex: 1, border: 'none', minHeight: '500px' }}
        />
      </div>
    </div>
  );
}

export function TemplatesPage() {
  const [search, setSearch] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<TemplateSummaryDto | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['templates', search],
    queryFn: () => templatesApi.list(search || undefined),
  });

  const templates = data?.templates ?? [];

  return (
    <div>
      {previewTemplate && (
        <PreviewModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>Templates</h1>
        <Link to="/templates/new" className="btn btn-primary">+ New Template</Link>
      </div>

      <input
        type="text"
        placeholder="Search templates…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: '1.5rem', maxWidth: '360px', display: 'block' }}
      />

      {isLoading ? (
        <p>Loading…</p>
      ) : templates.length === 0 ? (
        <div className="card">
          <p style={{ color: 'var(--color-text-muted)' }}>No templates found.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {templates.map((t) => (
            <div key={t.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Link to={`/templates/${t.id}`} style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {t.title}
                </Link>
                {t.description && (
                  <p style={{ color: 'var(--color-text-muted)', fontSize: '.875rem', marginTop: '.25rem' }}>
                    {t.description}
                  </p>
                )}
                <div style={{ marginTop: '.5rem', fontSize: '.8rem', color: 'var(--color-text-muted)' }}>
                  {t.fieldCount} field{t.fieldCount !== 1 ? 's' : ''} · v{t.version} ·{' '}
                  {new Date(t.updatedAt).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexShrink: 0, marginLeft: '1rem' }}>
                <button
                  className="btn btn-outline"
                  style={{ fontSize: '.8rem', padding: '.3rem .6rem' }}
                  onClick={() => setPreviewTemplate(t)}
                >
                  Preview
                </button>
                <span className={`badge ${t.status === 'active' ? 'badge-green' : 'badge-gray'}`}>
                  {t.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
